import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import CancelIcon from '@material-ui/icons/Cancel'
import { Spacer } from '@vestaboard/installables'
import React, { useEffect, useState } from 'react'

import { useLazyQuery } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'

import { useHawkbitTargets, IHawkbitTarget } from './hooks/useHawkbitTargets'
import { useHawkbitDistributionSets } from './hooks/useHawkbitDistributionSets'
import { useHawkbitTargetActions, IHawkbitAction } from './hooks/useHawkbitTargetActions'
import { useAssignHawkbitFirmware } from './hooks/useAssignHawkbitFirmware'
import { useCancelHawkbitAction } from './hooks/useCancelHawkbitAction'
import { DeviceBoardTitle, DeviceBoardTitleVariables } from './__generated__/DeviceBoardTitle'

const DEVICE_BOARD_TITLE_QUERY = gql`
  query DeviceBoardTitle($deviceId: String!) {
    device(id: $deviceId) {
      id
      firmwareVersion
      boards {
        id
        title
      }
    }
  }
`

const PAGE_SIZE = 25
const ACTIONS_PAGE_SIZE = 5

const useStyles = makeStyles(theme => ({
  clickableRow: {
    cursor: 'pointer',
    '&:focus': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`
    }
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected
  }
}))

const formatTimestamp = (ts: number | null) => {
  if (!ts) return '—'
  return new Date(ts).toLocaleString()
}

const statusColor = (status: string | null): 'default' | 'primary' | 'secondary' => {
  if (!status) return 'default'
  const s = status.toLowerCase()
  if (s === 'in_sync' || s === 'finished') return 'primary'
  if (s === 'error') return 'secondary'
  return 'default'
}

const DEFAULT_DS_ID = '136'

const TargetActionsPanel: React.FC<{
  target: IHawkbitTarget
}> = ({ target }) => {
  const [fetchActions, { data, loading, error }] = useHawkbitTargetActions()
  const { data: dsData } = useHawkbitDistributionSets({ limit: 100, offset: 0 })
  const distributionSets = dsData?.hawkbitDistributionSets?.content ?? []
  const [page, setPage] = useState(0)
  const [selectedDsId, setSelectedDsId] = useState(DEFAULT_DS_ID)
  const [upgrading, setUpgrading] = useState(false)
  const [upgradeResult, setUpgradeResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [cancellingId, setCancellingId] = useState<number | null>(null)
  const [polling, setPolling] = useState(false)
  const [confirmUpgrade, setConfirmUpgrade] = useState(false)
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null)
  const [assignFirmware] = useAssignHawkbitFirmware()
  const [cancelAction] = useCancelHawkbitAction()
  const [fetchBoardTitle, { data: boardData }] = useLazyQuery<DeviceBoardTitle, DeviceBoardTitleVariables>(DEVICE_BOARD_TITLE_QUERY, { fetchPolicy: 'cache-first' })
  const boardTitle = boardData?.device?.boards?.[0]?.title
  const currentFirmware = boardData?.device?.firmwareVersion

  const refetchActions = () => {
    fetchActions({ variables: { controllerId: target.controllerId, limit: ACTIONS_PAGE_SIZE, offset: page * ACTIONS_PAGE_SIZE } })
  }

  useEffect(() => {
    setPage(0)
    setUpgradeResult(null)
    setPolling(false)
    fetchActions({ variables: { controllerId: target.controllerId, limit: ACTIONS_PAGE_SIZE, offset: 0 } })
    fetchBoardTitle({ variables: { deviceId: target.controllerId } })
  }, [target, fetchActions, fetchBoardTitle])

  const actions = data?.hawkbitTargetActions?.content ?? []
  const total = data?.hawkbitTargetActions?.total ?? 0

  useEffect(() => {
    if (!polling) return
    const interval = setInterval(() => {
      refetchActions()
    }, 60000)
    return () => clearInterval(interval)
  }, [polling, target.controllerId, page])

  useEffect(() => {
    if (!polling || actions.length === 0) return
    const latest = actions[0]
    if (latest.status === 'finished' || latest.status === 'error' || latest.status === 'canceled') {
      setPolling(false)
    }
  }, [actions, polling])

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage)
    fetchActions({ variables: { controllerId: target.controllerId, limit: ACTIONS_PAGE_SIZE, offset: newPage * ACTIONS_PAGE_SIZE } })
  }

  const handleUpgrade = async () => {
    setUpgrading(true)
    setUpgradeResult(null)
    try {
      await assignFirmware({ variables: { input: { controllerId: target.controllerId, distributionSetId: selectedDsId } } })
      setUpgradeResult({ type: 'success', message: 'Upgrade queued' })
      setPolling(true)
      refetchActions()
    } catch (e: any) {
      setUpgradeResult({ type: 'error', message: e.message || 'Failed to assign firmware' })
    } finally {
      setUpgrading(false)
    }
  }

  const handleCancel = async (actionId: number) => {
    setCancellingId(actionId)
    try {
      await cancelAction({ variables: { input: { controllerId: target.controllerId, actionId: String(actionId) } } })
      refetchActions()
    } catch (e: any) {
      setUpgradeResult({ type: 'error', message: e.message || 'Failed to cancel action' })
    } finally {
      setCancellingId(null)
    }
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        {boardTitle || target.controllerId}
      </Typography>
      {boardTitle && (
        <Typography variant='caption' color='textSecondary' display='block' gutterBottom>
          {target.controllerId}
        </Typography>
      )}
      <Box mb={2}>
        <Typography variant='body2'>
          {currentFirmware && <><strong>{currentFirmware}</strong>{' · '}</>}
          Status: <Chip size='small' label={target.updateStatus || 'unknown'} color={statusColor(target.updateStatus)} />
          {' · '}IP: {target.ipAddress || '—'}
          {' · '}Last seen by HawkBit: {formatTimestamp(target.lastControllerRequestAt)}
        </Typography>
      </Box>
      <Box display='flex' alignItems='center' mb={2} style={{ gap: 8 }}>
        <Select
          value={selectedDsId}
          onChange={e => setSelectedDsId(e.target.value as string)}
          variant='outlined'
          style={{ minWidth: 200 }}>
          {distributionSets.map(ds => (
            <MenuItem key={ds.id} value={String(ds.id)}>
              {ds.version} ({ds.id})
            </MenuItem>
          ))}
        </Select>
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={() => {
            fetchBoardTitle({ variables: { deviceId: target.controllerId } })
            setConfirmUpgrade(true)
          }}
          disabled={upgrading}>
          {upgrading ? <CircularProgress size={18} /> : 'Upgrade'}
        </Button>
        {polling && (
          <Typography variant='caption' color='textSecondary'>
            Polling every 60s
          </Typography>
        )}
      </Box>
      {upgradeResult && (
        <Box mb={2}>
          <Alert severity={upgradeResult.type}>{upgradeResult.message}</Alert>
        </Box>
      )}
      {loading ? (
        <Box display='flex' justifyContent='center' p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity='error'>{error.message}</Alert>
      ) : actions.length === 0 ? (
        <Alert severity='info'>No upgrade actions found for this target.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Firmware</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {actions.map((action: IHawkbitAction) => (
                  <TableRow key={action.id}>
                    <TableCell>
                      {action.distributionSet
                        ? action.distributionSet.version || action.distributionSet.name || `ID ${action.distributionSet.id}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size='small' label={action.status || 'unknown'} color={statusColor(action.status)} />
                    </TableCell>
                    <TableCell>{action.type || '—'}</TableCell>
                    <TableCell>{formatTimestamp(action.createdAt)}</TableCell>
                    <TableCell>{formatTimestamp(action.lastModifiedAt)}</TableCell>
                    <TableCell padding='none'>
                      {action.status !== 'finished' && (
                        <Tooltip title='Cancel action'>
                          <IconButton
                            size='small'
                            onClick={() => setConfirmCancelId(action.id)}
                            disabled={cancellingId === action.id}>
                            {cancellingId === action.id ? <CircularProgress size={16} /> : <CancelIcon fontSize='small' />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={ACTIONS_PAGE_SIZE}
            rowsPerPageOptions={[ACTIONS_PAGE_SIZE]}
          />
        </>
      )}
      <Dialog open={confirmUpgrade} onClose={() => setConfirmUpgrade(false)}>
        <DialogTitle>Upgrade Target</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to upgrade{' '}
            {boardTitle ? <><strong>{boardTitle}</strong> ({target.controllerId})</> : <strong>{target.controllerId}</strong>}
            {currentFirmware ? <> from <strong>{currentFirmware}</strong></> : null}
            {' '}to firmware <strong>{distributionSets.find(ds => String(ds.id) === selectedDsId)?.version || selectedDsId}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpgrade(false)}>Cancel</Button>
          <Button
            color='primary'
            variant='contained'
            onClick={() => {
              setConfirmUpgrade(false)
              handleUpgrade()
            }}>
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmCancelId !== null} onClose={() => setConfirmCancelId(null)}>
        <DialogTitle>Cancel Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to force-cancel action {confirmCancelId} for device{' '}
            <strong>{target.controllerId}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelId(null)}>No</Button>
          <Button
            color='secondary'
            variant='contained'
            onClick={() => {
              if (confirmCancelId !== null) handleCancel(confirmCancelId)
              setConfirmCancelId(null)
            }}>
            Cancel Action
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const RECENT_SEARCHES_KEY = 'hawkbit-recent-searches'
const MAX_RECENT = 5

const getRecentSearches = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]')
  } catch {
    return []
  }
}

const addRecentSearch = (term: string): string[] => {
  const recent = getRecentSearches().filter(s => s !== term)
  recent.unshift(term)
  const trimmed = recent.slice(0, MAX_RECENT)
  try { localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(trimmed)) } catch {}
  return trimmed
}

const TargetsTab: React.FC = () => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState<string | null>(null)
  const [selectedTarget, setSelectedTarget] = useState<IHawkbitTarget | null>(null)
  const [recentSearches, setRecentSearches] = useState<string[]>(getRecentSearches)

  const { data, loading, error, refetch } = useHawkbitTargets({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    query
  })

  const targets = data?.hawkbitTargets?.content ?? []
  const total = data?.hawkbitTargets?.total ?? 0

  useEffect(() => {
    if (targets.length > 0 && !selectedTarget) {
      setSelectedTarget(targets[0])
    }
  }, [targets])

  const handleSearch = (term?: string) => {
    const value = term ?? search
    setPage(0)
    if (value.trim()) {
      if (term) setSearch(term)
      const escaped = value.trim().replace(/["\\]/g, '\\$&')
      setQuery(`name=="*${escaped}*"`)
      setRecentSearches(addRecentSearch(value.trim()))
    } else {
      setQuery(null)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleClearSearch = () => {
    setSearch('')
    setQuery(null)
    setPage(0)
  }

  return (
    <Box display='flex' style={{ gap: 24 }}>
      <Box style={{ flex: '1 1 0', minWidth: 0 }}>
        <Box display='flex' alignItems='center' mb={2}>
          <TextField
            size='small'
            variant='outlined'
            placeholder='Search targets...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ flex: 1, marginRight: 8 }}
          />
          <Button variant='contained' color='primary' onClick={() => handleSearch()} size='small' style={{ marginRight: 8 }}>
            Search
          </Button>
          {query && (
            <Button variant='outlined' onClick={handleClearSearch} size='small'>
              Clear
            </Button>
          )}
          <Box flexGrow={1} />
          <Button variant='outlined' onClick={() => refetch()} size='small'>
            Refresh
          </Button>
        </Box>
        {recentSearches.length > 0 && (
          <Box display='flex' alignItems='center' mb={1} style={{ gap: 4 }}>
            <Typography variant='caption' color='textSecondary' style={{ marginRight: 4 }}>
              Recent:
            </Typography>
            {recentSearches.map(term => (
              <Chip
                key={term}
                label={term}
                size='small'
                variant='outlined'
                onClick={() => handleSearch(term)}
                style={{ fontSize: 11 }}
              />
            ))}
          </Box>
        )}

        {loading ? (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity='error'>{error.message}</Alert>
        ) : targets.length === 0 ? (
          <Alert severity='info'>No targets found.</Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Device ID</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {targets.map(target => (
                    <TableRow
                      key={target.controllerId}
                      hover
                      tabIndex={0}
                      selected={selectedTarget?.controllerId === target.controllerId}
                      aria-label={`View actions for ${target.controllerId}`}
                      className={`${classes.clickableRow} ${selectedTarget?.controllerId === target.controllerId ? classes.selectedRow : ''}`}
                      onClick={() => setSelectedTarget(target)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedTarget(target)
                        }
                      }}>
                      <TableCell>
                        {target.controllerId}
                        {target.name && target.name !== target.controllerId && (
                          <Typography variant='caption' display='block' color='textSecondary'>
                            {target.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size='small' label={target.updateStatus || 'unknown'} color={statusColor(target.updateStatus)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </>
        )}
      </Box>

      <Box style={{ flex: '1 1 0', minWidth: 0 }}>
        {selectedTarget ? (
          <TargetActionsPanel target={selectedTarget} />
        ) : (
          <Box display='flex' justifyContent='center' alignItems='center' p={4}>
            <Typography color='textSecondary'>Select a target to view actions</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const FirmwareTab: React.FC = () => {
  const [page, setPage] = useState(0)

  const { data, loading, error, refetch } = useHawkbitDistributionSets({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE
  })

  const sets = data?.hawkbitDistributionSets?.content ?? []
  const total = data?.hawkbitDistributionSets?.total ?? 0

  return (
    <>
      <Box display='flex' justifyContent='flex-end' mb={2}>
        <Button variant='outlined' onClick={() => refetch()}>
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box display='flex' justifyContent='center' p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity='error'>{error.message}</Alert>
      ) : sets.length === 0 ? (
        <Alert severity='info'>No distribution sets found.</Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Complete</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Modified</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sets.map(ds => (
                  <TableRow key={ds.id}>
                    <TableCell>{ds.id}</TableCell>
                    <TableCell>{ds.name}</TableCell>
                    <TableCell>
                      <Chip size='small' label={ds.version} />
                    </TableCell>
                    <TableCell>{ds.description || '—'}</TableCell>
                    <TableCell>{ds.complete ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{formatTimestamp(ds.createdAt)}</TableCell>
                    <TableCell>{formatTimestamp(ds.lastModifiedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </>
      )}
    </>
  )
}

export const HawkbitManager: React.FC = () => {
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <Typography variant='h4'>Hawkbit</Typography>
      <Spacer size='large' />
      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor='primary' textColor='primary'>
          <Tab label='Targets' />
          <Tab label='Firmware' />
        </Tabs>
      </Paper>
      <Spacer size='large' />
      {tab === 0 && <TargetsTab />}
      {tab === 1 && <FirmwareTab />}
    </Box>
  )
}
